/**
 * `useCurrentMember` — resolve "who am I on this team" for the task Map.
 *
 * Issue #761 (Phase 2). The member views need two facts about the signed-in
 * user, both relative to the current team:
 *
 *   - their **membership id** — to filter `assignments` down to "my work"
 *     (B5.1) and to highlight "my parts" on the full map (B5.2). Task
 *     assignments carry only `membership_id`, so we map the auth account id
 *     to a membership row via `MemberRow.account_id === auth.accountId`
 *     (the same self-identification the rostering side uses, e.g.
 *     `CycleNotesPanel`'s `note.author_account_id === currentAccountId`).
 *   - their **role** on the team (`lead` / `co_lead` vs `member`) — to choose
 *     the experience: a lead gets the full two-way editor (#759/#760); a
 *     member gets the My-work + read-map experience. This reuses the
 *     existing `useTeam().role`, the same source `RetrospectivePillSection` /
 *     `TeamAuditTickerSection` use for `isLead`.
 *
 * Centralising role + identity here keeps the role-branching obvious and in
 * ONE place (the founder's "no scattered conditionals" bar). It depends only
 * on `useAuth`, `useTeam`, and the taskmap-local `useTaskTeamMembers` query —
 * no scheduler coupling.
 */

import { useTeam } from "../../api/useTeam";
import { useAuth } from "../../lib/AuthContext";
import { useTaskTeamMembers } from "./useTaskTeamMembers";

export interface CurrentMember {
  /** This user's membership id on the team, or null if not resolved / not a member. */
  membershipId: string | null;
  /** True when the user is the team `lead` / `co_lead`. */
  isLead: boolean;
  /** Display name of this user on the team (for "you are <name>"), or null. */
  displayName: string | null;
  /** Initials for the "you are" avatar, or null. */
  initials: string | null;
  /** True while role / membership are still resolving (gate effects on this). */
  loading: boolean;
}

/**
 * Resolve the current user's membership + role on a team. Returns a stable,
 * derived value (no internal state) so callers can branch the experience.
 */
export function useCurrentMember(teamId: string | undefined): CurrentMember {
  const { accountId, loading: authLoading } = useAuth();
  const teamQuery = useTeam(teamId);
  const { list, isLoading: membersLoading } = useTaskTeamMembers(teamId);

  const role = teamQuery.data?.role;
  const isLead = role === "lead" || role === "co_lead";

  // The roster rows the avatar query exposes don't carry `account_id`, so we
  // re-derive the self row from the raw member list it loads. `useTaskTeamMembers`
  // already filtered out "left" rows, so a match here is an active membership.
  // (We read the same `MemberRow[]` it consumes; matching on `account_id` is
  // the established self-identification pattern.)
  const self = accountId ? list.find((m) => m.accountId === accountId) : undefined;

  return {
    membershipId: self?.membershipId ?? null,
    isLead,
    displayName: self?.name ?? null,
    initials: self?.initials ?? null,
    loading: authLoading || teamQuery.isLoading || membersLoading,
  };
}
